import {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import RelativePortal from "react-relative-portal";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
type Props = {
  component?: ReactNode | null;
  text?: string;
  children: ReactNode;
  style?: CSSProperties;
  placement?: "top" | "bottom" | "right";
  horizontalOffset?: number;
  verticalOffset?: number;
  arrow?: boolean;
  shouldHide?: boolean;
  delay?: number;
};

const DEFAULT_VERTICAL_OFFSET = 44;

export const Tooltip: FC<Props> = ({
  children,
  component,
  text,
  style = {},
  placement = "top",
  horizontalOffset = 8,
  verticalOffset = 0,
  arrow = false,
  shouldHide = false,
  delay = 300,
}: Props) => {
  const [visible, setVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number>();
  const onMouseEnter = useCallback(() => {
    const timeoutId: number = window.setTimeout(() => {
      setVisible(true);
    }, delay);
    setTimeoutId(timeoutId);
  }, [delay]);
  const onMouseLeave = useCallback(() => {
    clearTimeout(timeoutId);
    setVisible(false);
  }, [timeoutId]);

  const top = useMemo(() => {
    if (placement === "top") {
      return -DEFAULT_VERTICAL_OFFSET - verticalOffset;
    } else if (placement === "bottom") {
      return DEFAULT_VERTICAL_OFFSET + verticalOffset;
    }
    return verticalOffset;
  }, [placement, verticalOffset]);

  const left = useMemo(() => {
    const horizontalOffsetInPx = `${horizontalOffset}px`;
    if (placement === "right") {
      return `calc(100% + ${horizontalOffsetInPx})`;
    }
    return horizontalOffset;
  }, [placement, horizontalOffset]);

  return (
    <span
      style={{ position: "relative", ...style }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {visible && !shouldHide && (component || text) && (
        <span
          style={{
            position: "absolute",
            top,
            left,
            cursor: "unset",
            zIndex: 1000,
          }}
        >
          {/* @ts-ignore-next-line */}
          <RelativePortal component="div">
            {component ? (
              <div
                className={cn(
                  "absolute bg-white text-white py-2 px-3 whitespace-nowrap shadow-lg rounded-xl mx-auto left-1/2 z-[10000]",
                  arrow &&
                    ["top", "bottom"].includes(placement) &&
                    "-translate-x-1/2",
                  arrow &&
                    placement === "top" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:left-1/2 after:top-full after:border-t-[6px] after:border-t-white",
                  arrow &&
                    placement === "bottom" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:left-1/2 after:bottom-full after:border-b-[6px] after:border-b-white",
                  arrow &&
                    placement === "right" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:right-full after:-mt-3 after:border-r-[6px] after:border-r-white",
                )}
              >
                {component}
              </div>
            ) : (
              <div
                className={cn(
                  "absolute bg-white text-white py-2 px-3 whitespace-nowrap shadow-lg rounded-xl mx-auto left-1/2 z-[10000]",
                  arrow &&
                    ["top", "bottom"].includes(placement) &&
                    "-translate-x-1/2",
                  arrow &&
                    placement === "top" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:left-1/2 after:top-full after:border-t-[6px] after:border-t-white",
                  arrow &&
                    placement === "bottom" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:left-1/2 after:bottom-full after:border-b-[6px] after:border-b-white",
                  arrow &&
                    placement === "right" &&
                    "after:content-[''] after:z-[10000] after:absolute after:-ml-1.5 after:w-0 after:h-0 after:border-[6px] after:border-solid after:border-transparent after:right-full after:-mt-3 after:border-r-[6px] after:border-r-white",
                )}
              >
                <Text variant="compact-title" className="text-black">
                  {text}
                </Text>
              </div>
            )}
          </RelativePortal>
        </span>
      )}
      {children}
    </span>
  );
};

export default Tooltip;
